import { Box, Container, Typography, Button, Grid } from "@mui/material";
import { Link } from "react-router-dom";
import { useDocumentTitle } from "../../utils/data";
import { keyframes } from "@mui/system";
// import TalentManagementImg from "../../assets/images/TalentManagement.jpeg";
// import IndustryFallbackImg from "../../assets/images/industryimg.jpg";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const scaleInCenter = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
`;

const bubbleFloat = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0); }
`;

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const expandWidth = keyframes`
  from { width: 0; opacity: 0; }
  to { width: 100%; opacity: 1; }
`;

const featuresData = [
  { icon: "🔍", title: "Talent Acquisition", description: "Advanced sourcing and candidate management tools" },
  { icon: "📋", title: "Applicant Tracking", description: "Streamlined ATS with AI-powered matching" },
  { icon: "👥", title: "Onboarding", description: "Automated employee onboarding workflows" },
  { icon: "📊", title: "Analytics", description: "Real-time HR analytics and insights" },
  { icon: "💰", title: "Payroll Integration", description: "Seamless payroll and compensation management" },
  { icon: "📈", title: "Performance", description: "Performance tracking and review management" },
];

const processSteps = [
  { step: "01", title: "Post Job", description: "Create and publish job openings to multiple channels" },
  { step: "02", title: "Screen Candidates", description: "AI-powered resume screening and ranking" },
  { step: "03", title: "Interview", description: "Schedule and conduct interviews seamlessly" },
  { step: "04", title: "Evaluate", description: "Collaborative candidate evaluation and feedback" },
  { step: "05", title: "Offer", description: "Generate and send customized offer letters" },
  { step: "06", title: "Onboard", description: "Smooth employee onboarding and integration" },
];

const benefitsData = [
  { title: "Reduce Time-to-Hire", emoji: "⚡", benefit: "Decrease hiring cycle by 40% with automation" },
  { title: "Improve Candidate Quality", emoji: "✨", benefit: "AI-powered matching for better fits" },
  { title: "Enhanced Retention", emoji: "🎯", benefit: "Better onboarding leads to 25% higher retention" },
  { title: "Cost Savings", emoji: "💵", benefit: "Reduce recruitment costs by 35%" },
  { title: "Better Culture Fit", emoji: "🤝", benefit: "Comprehensive assessment tools" },
  { title: "Scalable Growth", emoji: "📈", benefit: "Handle growth without hiring overhead" },
];

const TalentManagement = () => {
  useDocumentTitle("Briozon - Talent Management");

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Floating decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
            animation: `${floatBall} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "3%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(118,75,162,0.1) 0%, rgba(91,124,250,0.1) 100%)",
            animation: `${floatBall} 6s ease-in-out infinite 1s`,
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: { xs: "12px", sm: "14px" },
                    fontWeight: 700,
                    color: "#5b7cfa",
                    letterSpacing: "2px",
                    mb: 1,
                    display: "block",
                    animation: `${fadeUp} 0.6s ease-out 0.05s both`,
                  }}
                >
                  TALENT MANAGEMENT
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "32px", sm: "42px", md: "48px" },
                    fontWeight: 800,
                    color: "#1b1f33",
                    lineHeight: 1.2,
                    mb: 2,
                    animation: `${fadeUp} 0.6s ease-out 0.1s both`,
                  }}
                >
                  Smarter, Faster,{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Together
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    color: "#4c5472",
                    lineHeight: 1.8,
                    mb: 3,
                    animation: `${fadeUp} 0.6s ease-out 0.2s both`,
                  }}
                >
                  With our top-notch service, Briozon connects you to exceptional talent. 
                  Experience tailored solutions that elevate your organization to new heights.
                </Typography>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: "16px",
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                    animation: `${fadeUp} 0.6s ease-out 0.3s both`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                    },
                  }}
                >
                  Get Started Today
                </Button>
              </Box>
            </Grid>

            {/* Right - Creative Visual */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 320, sm: 400, md: 450 },
                  animation: `${scaleInCenter} 0.8s ease-out 0.2s both`,
                }}
              >
                {/* Main gradient circle */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 220, sm: 280, md: 320 },
                    height: { xs: 220, sm: 280, md: 320 },
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    filter: "blur(40px)",
                  }}
                />

                {/* Central icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 100, sm: 120 },
                    height: { xs: 100, sm: 120 },
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "48px", sm: "56px" },
                    boxShadow: "0 20px 40px rgba(91, 124, 250, 0.4)",
                    animation: `${floatBall} 6s ease-in-out infinite`,
                    zIndex: 2,
                  }}
                >
                  👥
                </Box>

                {/* Floating stat cards */}
                <Box
                  sx={{
                    position: "absolute",
                    top: { xs: "5%", sm: "8%" },
                    left: { xs: "5%", sm: "10%" },
                    p: 2,
                    background: "rgba(255,255,255,0.95)",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    animation: `${floatBall} 5s ease-in-out infinite 0.5s`,
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: "24px", sm: "32px" }, fontWeight: 800, color: "#5b7cfa", lineHeight: 1 }}>
                    40%
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#4b5563", fontWeight: 600 }}>
                    Faster Hiring
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: { xs: "5%", sm: "5%" },
                    right: { xs: "5%", sm: "8%" },
                    p: 2,
                    background: "rgba(255,255,255,0.95)",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    animation: `${floatBall} 6s ease-in-out infinite 1s`,
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: "24px", sm: "32px" }, fontWeight: 800, color: "#764ba2", lineHeight: 1 }}>
                    400+
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#4b5563", fontWeight: 600 }}>
                    Placements
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: { xs: "8%", sm: "10%" },
                    left: { xs: "8%", sm: "15%" },
                    p: 2,
                    background: "rgba(255,255,255,0.95)",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    animation: `${floatBall} 7s ease-in-out infinite 1.5s`,
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: "24px", sm: "32px" }, fontWeight: 800, color: "#5b7cfa", lineHeight: 1 }}>
                    98%
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#4b5563", fontWeight: 600 }}>
                    Client Satisfaction
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: { xs: "5%", sm: "8%" },
                    right: { xs: "5%", sm: "10%" },
                    p: 2,
                    background: "rgba(255,255,255,0.95)",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    animation: `${floatBall} 5.5s ease-in-out infinite 2s`,
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: "24px", sm: "32px" }, fontWeight: 800, color: "#764ba2", lineHeight: 1 }}>
                    35%
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#4b5563", fontWeight: 600 }}>
                    Cost Savings
                  </Typography>
                </Box>

                {/* Floating icons */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "60%",
                    left: { xs: "15%", sm: "20%" },
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.2) 0%, rgba(118,75,162,0.2) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    animation: `${floatBall} 6s ease-in-out infinite 0.3s`,
                  }}
                >
                  🔍
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "62%",
                    right: { xs: "15%", sm: "18%" },
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, rgba(118,75,162,0.2) 0%, rgba(91,124,250,0.2) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    animation: `${floatBall} 4.5s ease-in-out infinite 1.8s`,
                  }}
                >
                  📋
                </Box>

                {/* Decorative rings */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 200, sm: 260 },
                    height: { xs: 200, sm: 260 },
                    borderRadius: "50%",
                    border: "2px dashed rgba(91, 124, 250, 0.2)",
                    animation: `${floatBall} 10s ease-in-out infinite`,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 280, sm: 360 },
                    height: { xs: 280, sm: 360 },
                    borderRadius: "50%",
                    border: "1px dashed rgba(118, 75, 162, 0.15)",
                    animation: `${floatBall} 12s ease-in-out infinite reverse`,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Tailored Solutions Banner — WOW */}
      <Box sx={{ py: { xs: 6, sm: 8 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              position: "relative",
              borderRadius: "28px",
              overflow: "hidden",
              background: "linear-gradient(135deg, #3d2fa0 0%, #5b7cfa 45%, #764ba2 100%)",
              boxShadow: "0 24px 80px rgba(91,124,250,0.35)",
              animation: `${fadeUp} 0.6s ease-out 0.05s both`,
            }}
          >
            {/* dot grid overlay */}
            <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)", backgroundSize: "32px 32px", pointerEvents: "none" }} />
            {/* glow blobs */}
            <Box sx={{ position: "absolute", top: "-80px", left: "-60px", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)", pointerEvents: "none" }} />
            <Box sx={{ position: "absolute", bottom: "-60px", right: "30%", width: 220, height: 220, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)", pointerEvents: "none" }} />

            <Grid container alignItems="center">
              {/* LEFT — text content */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: { xs: 4, sm: 5, md: 6 }, position: "relative", zIndex: 2 }}>
                  {/* overline chip */}
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 2, py: 0.7, borderRadius: "999px", background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.25)", mb: 3 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#a5f3fc", boxShadow: "0 0 6px #a5f3fc", animation: `${bubbleFloat} 2s ease-in-out infinite` }} />
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#fff", letterSpacing: 2 }}>TAILORED FOR YOUR BUSINESS</Typography>
                  </Box>

                  <Typography sx={{ fontSize: { xs: "28px", sm: "36px", md: "44px" }, fontWeight: 900, color: "#fff", lineHeight: 1.2, mb: 2.5 }}>
                    Every Business is{" "}
                    <Box component="span" sx={{ position: "relative", display: "inline-block",
                      "&::after": { content: '""', position: "absolute", bottom: "-4px", left: 0, right: 0, height: "3px", background: "rgba(255,255,255,0.6)", borderRadius: "2px", animation: `${expandWidth} 1s ease-out 0.5s both` }
                    }}>
                      Unique.
                    </Box>
                    <br />So Is Our Solution.
                  </Typography>

                  <Typography sx={{ fontSize: { xs: "15px", sm: "16px" }, color: "rgba(255,255,255,0.85)", lineHeight: 1.8, mb: 4, maxWidth: 440 }}>
                    Our specialists evaluate your market dynamics, culture, and growth goals to build a talent strategy that brings exactly the right people — not just available candidates.
                  </Typography>

                  {/* 3 quick-stat pills */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 4 }}>
                    {[
                      { val: "400+", label: "Placements" },
                      { val: "98%", label: "Satisfaction" },
                      { val: "10+", label: "Years Expert" },
                    ].map((s) => (
                      <Box key={s.label} sx={{ px: 2.5, py: 1, borderRadius: "999px", background: "rgba(255,255,255,0.15)", backdropFilter: "blur(6px)", border: "1px solid rgba(255,255,255,0.2)" }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 800, color: "#fff" }}>{s.val} <Box component="span" sx={{ fontWeight: 500, opacity: 0.8 }}>{s.label}</Box></Typography>
                      </Box>
                    ))}
                  </Box>

                  <Button
                    component={Link}
                    to="/contact"
                    variant="contained"
                    sx={{
                      backgroundColor: "#fff",
                      color: "#5b7cfa",
                      fontWeight: 800,
                      px: 4,
                      py: 1.5,
                      borderRadius: "14px",
                      fontSize: "15px",
                      textTransform: "none",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                      transition: "all 0.3s ease",
                      "&:hover": { backgroundColor: "#f0f0f0", transform: "translateY(-3px)", boxShadow: "0 14px 32px rgba(0,0,0,0.25)" },
                    }}
                  >
                    Talk to Our Specialists →
                  </Button>
                </Box>
              </Grid>

              {/* RIGHT — platform mockup */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: { xs: 3, sm: 4, md: 5 }, position: "relative", zIndex: 2, display: "flex", justifyContent: "center" }}>
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: 420,
                      borderRadius: "20px",
                      background: "rgba(255,255,255,0.10)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      p: 3,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                      animation: `${bubbleFloat} 5s ease-in-out infinite`,
                    }}
                  >
                    {/* Mock header */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#fff", letterSpacing: 1 }}>🎯 AI Candidate Matches</Typography>
                      <Box sx={{ px: 1.5, py: 0.4, borderRadius: "999px", background: "rgba(165,243,252,0.2)", border: "1px solid rgba(165,243,252,0.4)" }}>
                        <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#a5f3fc" }}>● LIVE</Typography>
                      </Box>
                    </Box>

                    {/* Candidate cards */}
                    {[
                      { title: "Senior Product Manager", exp: "8 yrs exp · Remote", match: 97, avatar: "👩‍💼", skills: ["Strategy", "Agile"] },
                      { title: "Full-Stack Engineer", exp: "5 yrs exp · Hybrid", match: 94, avatar: "👨‍💻", skills: ["React", "Node.js"] },
                      { title: "Data Scientist", exp: "4 yrs exp · On-site", match: 91, avatar: "👩‍🔬", skills: ["ML", "Python"] },
                    ].map((c, i) => (
                      <Box
                        key={c.title}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          p: 1.5,
                          borderRadius: "14px",
                          background: i === 0 ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.08)",
                          border: i === 0 ? "1px solid rgba(255,255,255,0.35)" : "1px solid rgba(255,255,255,0.1)",
                          mb: 1.5,
                          transition: "all 0.3s ease",
                          animation: `${fadeUp} 0.5s ease-out ${0.1 * (i + 1)}s both`,
                        }}
                      >
                        <Typography sx={{ fontSize: "28px", lineHeight: 1 }}>{c.avatar}</Typography>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>{c.title}</Typography>
                          <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.65)", mb: 0.5 }}>{c.exp}</Typography>
                          <Box sx={{ display: "flex", gap: 0.8 }}>
                            {c.skills.map((sk) => (
                              <Box key={sk} sx={{ px: 1, py: 0.2, borderRadius: "999px", background: "rgba(255,255,255,0.12)" }}>
                                <Typography sx={{ fontSize: "9px", fontWeight: 600, color: "rgba(255,255,255,0.8)" }}>{sk}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                        {/* Match score */}
                        <Box sx={{ textAlign: "center", minWidth: 44 }}>
                          <Typography sx={{ fontSize: "18px", fontWeight: 900, color: c.match >= 95 ? "#a5f3fc" : "#fff", lineHeight: 1 }}>{c.match}%</Typography>
                          <Typography sx={{ fontSize: "8px", color: "rgba(255,255,255,0.6)", fontWeight: 600 }}>MATCH</Typography>
                        </Box>
                      </Box>
                    ))}

                    {/* Footer */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2, pt: 2, borderTop: "1px solid rgba(255,255,255,0.15)" }}>
                      <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.6)" }}>Analyzing 2,847 profiles…</Typography>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        {[0, 1, 2, 3].map((d) => (
                          <Box key={d} sx={{ width: 4, height: 16 + d * 4, borderRadius: "2px", background: "rgba(255,255,255,0.4)", animation: `${bubbleFloat} ${1.2 + d * 0.2}s ease-in-out infinite`, animationDelay: `${d * 0.15}s` }} />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Strategic Talent Architecture Section */}
      <Box sx={{ py: { xs: 8, sm: 10, md: 12 }, background: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, sm: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#1b1f33",
                mb: 2,
                animation: `${fadeUp} 0.6s ease-out 0.05s both`,
              }}
            >
              Strategic Talent{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Architecture
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                color: "#4c5472",
                maxWidth: "600px",
                mx: "auto",
                animation: `${fadeUp} 0.6s ease-out 0.15s both`,
              }}
            >
              Comprehensive talent solutions tailored to your organization's unique needs
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2.5, sm: 3 }}>
            {[
              { icon: "📄", title: "Contract to Hire", desc: "Flexible hiring with seamless permanent transitions" },
              { icon: "👔", title: "Permanent Staffing", desc: "Long-term talent acquisition for lasting success" },
              { icon: "⚙️", title: "Supplier Optimization", desc: "Streamlined vendor management and cost efficiency" },
              { icon: "🚀", title: "Hire. Build. Deliver", desc: "End-to-end talent lifecycle management" },
              { icon: "🤝", title: "Contract Talent Acquisition", desc: "Rapid deployment of skilled contract professionals" },
              { icon: "📊", title: "Resource Allocation Strategy", desc: "Data-driven workforce planning and optimization" },
            ].map((item, idx) => (
              <Grid item xs={12} sm={6} md={4} key={item.title}>
                <Box
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    minHeight: { xs: "auto", sm: 110 },
                    display: "flex",
                    alignItems: "center",
                    background: "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)",
                    borderRadius: "16px",
                    border: "1px solid rgba(91, 124, 250, 0.15)",
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    animation: `${scaleInCenter} 0.5s ease-out ${0.08 * idx}s both`,
                    cursor: "pointer",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "4px",
                      background: "linear-gradient(90deg, #5b7cfa 0%, #764ba2 100%)",
                      transform: "scaleX(0)",
                      transformOrigin: "left",
                      transition: "transform 0.4s ease",
                    },
                    "&:hover": {
                      transform: "translateY(-6px) scale(1.02)",
                      boxShadow: "0 20px 40px rgba(91, 124, 250, 0.18)",
                      background: "linear-gradient(135deg, #fff 0%, #f8faff 100%)",
                      "&::before": {
                        transform: "scaleX(1)",
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: { xs: "32px", sm: "38px" },
                        animation: `${bubbleFloat} 3s ease-in-out infinite`,
                        animationDelay: `${0.2 * idx}s`,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: { xs: "16px", sm: "19px", md: "20px" },
                          fontWeight: 700,
                          color: "#1f2937",
                          mb: 0.5,
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: "16px", sm: "17px" },
                          color: "#4b5563",
                          lineHeight: 1.5,
                        }}
                      >
                        {item.desc}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Process/Workflow Section - Timeline Style */}
      <Box sx={{ py: { xs: 8, sm: 10, md: 12 }, background: "linear-gradient(135deg, #f6f8ff 0%, #eef3ff 100%)", overflow: "hidden" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 6, sm: 8 } }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#1b1f33",
                mb: 2,
                animation: `${fadeUp} 0.6s ease-out 0.05s both`,
              }}
            >
              Recruitment{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Workflow
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                color: "#4c5472",
                maxWidth: "600px",
                mx: "auto",
                animation: `${fadeUp} 0.6s ease-out 0.15s both`,
              }}
            >
              Streamlined 6-step process from posting to onboarding
            </Typography>
          </Box>

          {/* Timeline Container */}
          <Box
            sx={{
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                left: { xs: "24px", md: "50%" },
                transform: { xs: "none", md: "translateX(-50%)" },
                top: 0,
                bottom: 0,
                width: "3px",
                background: "linear-gradient(180deg, #5b7cfa 0%, #764ba2 50%, #5b7cfa 100%)",
                borderRadius: "4px",
              },
            }}
          >
            {processSteps.map((item, idx) => (
              <Box
                key={item.step}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "row", md: idx % 2 === 0 ? "row" : "row-reverse" },
                  alignItems: "center",
                  mb: { xs: 4, sm: 5 },
                  position: "relative",
                  animation: `${idx % 2 === 0 ? slideInLeft : slideInRight} 0.6s ease-out ${0.15 * idx}s both`,
                }}
              >
                {/* Content */}
                <Box
                  sx={{
                    flex: 1,
                    pl: { xs: 7, md: idx % 2 === 0 ? 0 : 6 },
                    pr: { xs: 0, md: idx % 2 === 0 ? 6 : 0 },
                    textAlign: { xs: "left", md: idx % 2 === 0 ? "right" : "left" },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: "18px", sm: "22px" },
                      fontWeight: 800,
                      color: "#1f2937",
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "flex-start", md: idx % 2 === 0 ? "flex-end" : "flex-start" },
                      gap: 1.5,
                    }}
                  >
                    {idx % 2 !== 0 && (
                      <Box
                        component="span"
                        sx={{
                          display: { xs: "none", md: "inline" },
                          fontSize: "28px",
                          animation: `${bubbleFloat} 3s ease-in-out infinite`,
                          animationDelay: `${0.2 * idx}s`,
                        }}
                      >
                        {["📝", "🔍", "💬", "📊", "📧", "🎉"][idx]}
                      </Box>
                    )}
                    {item.title}
                    {idx % 2 === 0 && (
                      <Box
                        component="span"
                        sx={{
                          display: { xs: "none", md: "inline" },
                          fontSize: "28px",
                          animation: `${bubbleFloat} 3s ease-in-out infinite`,
                          animationDelay: `${0.2 * idx}s`,
                        }}
                      >
                        {["📝", "🔍", "💬", "📊", "📧", "🎉"][idx]}
                      </Box>
                    )}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "16px", sm: "17px" },
                      color: "#4b5563",
                      lineHeight: 1.7,
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>

                {/* Center Circle with Step Number */}
                <Box
                  sx={{
                    position: { xs: "absolute", md: "relative" },
                    left: { xs: 0, md: "auto" },
                    width: { xs: "48px", sm: "56px" },
                    height: { xs: "48px", sm: "56px" },
                    flexShrink: 0,
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: 900,
                    fontSize: { xs: "18px", sm: "22px" },
                    boxShadow: "0 8px 24px rgba(91, 124, 250, 0.35)",
                    zIndex: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.15)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.45)",
                    },
                  }}
                >
                  {item.step}
                </Box>

                {/* Spacer for opposite side */}
                <Box sx={{ flex: 1, display: { xs: "none", md: "block" } }} />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Features Section - Icon List Style */}
      <Box sx={{ py: { xs: 8, sm: 10, md: 12 }, background: "#f8fafc", overflow: "hidden" }}>
        <Container maxWidth="lg">
          {/* Heading on Top */}
          <Box sx={{ textAlign: "center", mb: { xs: 5, sm: 6 }, animation: `${fadeUp} 0.6s ease-out 0.05s both` }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#1b1f33",
                mb: 2,
              }}
            >
              Key{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Features
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                color: "#4c5472",
                lineHeight: 1.8,
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              Comprehensive tools designed to streamline your entire talent lifecycle
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 4, sm: 6 }}>
            {/* Left Column - 3 features */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2.5, sm: 3 } }}>
                {featuresData.slice(0, 3).map((feature, idx) => (
                  <Box
                    key={feature.title}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: { xs: 2, sm: 3 },
                      p: { xs: 2, sm: 2.5 },
                      borderRadius: "12px",
                      border: "1px solid transparent",
                      background: "linear-gradient(#fff, #fff) padding-box, linear-gradient(135deg, rgba(91,124,250,0.3) 0%, rgba(118,75,162,0.3) 100%) border-box",
                      transition: "all 0.3s ease",
                      animation: `${slideInLeft} 0.5s ease-out ${0.1 * idx}s both`,
                      cursor: "pointer",
                      position: "relative",
                      "&:hover": {
                        background: "linear-gradient(rgba(91,124,250,0.04), rgba(118,75,162,0.04)) padding-box, linear-gradient(135deg, rgba(91,124,250,0.5) 0%, rgba(118,75,162,0.5) 100%) border-box",
                        transform: "translateX(8px)",
                        "& .feature-icon": {
                          transform: "scale(1.15) rotate(5deg)",
                          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                        },
                      },
                    }}
                  >
                    <Box
                      className="feature-icon"
                      sx={{
                        width: { xs: 52, sm: 60 },
                        height: { xs: 52, sm: 60 },
                        borderRadius: "14px",
                        background: "linear-gradient(135deg, rgba(91,124,250,0.12) 0%, rgba(118,75,162,0.12) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: { xs: "28px", sm: "32px" },
                        flexShrink: 0,
                        transition: "all 0.3s ease",
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Box sx={{ pt: 0.5 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: { xs: "20px", sm: "22px" },
                          fontWeight: 700,
                          color: "#1f2937",
                          mb: 0.5,
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: "16px", sm: "17px" },
                          color: "#4b5563",
                          lineHeight: 1.6,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Right Column - 3 features */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2.5, sm: 3 } }}>
                {featuresData.slice(3, 6).map((feature, idx) => (
                  <Box
                    key={feature.title}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: { xs: 2, sm: 3 },
                      p: { xs: 2, sm: 2.5 },
                      borderRadius: "12px",
                      border: "1px solid transparent",
                      background: "linear-gradient(#fff, #fff) padding-box, linear-gradient(135deg, rgba(91,124,250,0.3) 0%, rgba(118,75,162,0.3) 100%) border-box",
                      transition: "all 0.3s ease",
                      animation: `${slideInRight} 0.5s ease-out ${0.1 * idx}s both`,
                      cursor: "pointer",
                      position: "relative",
                      "&:hover": {
                        background: "linear-gradient(rgba(91,124,250,0.04), rgba(118,75,162,0.04)) padding-box, linear-gradient(135deg, rgba(91,124,250,0.5) 0%, rgba(118,75,162,0.5) 100%) border-box",
                        transform: "translateX(8px)",
                        "& .feature-icon": {
                          transform: "scale(1.15) rotate(5deg)",
                          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                        },
                      },
                    }}
                  >
                    <Box
                      className="feature-icon"
                      sx={{
                        width: { xs: 52, sm: 60 },
                        height: { xs: 52, sm: 60 },
                        borderRadius: "14px",
                        background: "linear-gradient(135deg, rgba(91,124,250,0.12) 0%, rgba(118,75,162,0.12) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: { xs: "28px", sm: "32px" },
                        flexShrink: 0,
                        transition: "all 0.3s ease",
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Box sx={{ pt: 0.5 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: { xs: "20px", sm: "22px" },
                          fontWeight: 700,
                          color: "#1f2937",
                          mb: 0.5,
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: "16px", sm: "17px" },
                          color: "#4b5563",
                          lineHeight: 1.6,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: { xs: 8, sm: 10, md: 12 }, background: "linear-gradient(135deg, #f6f8ff 0%, #eef3ff 100%)", overflow: "hidden" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, sm: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#1b1f33",
                mb: 2,
                animation: `${fadeUp} 0.6s ease-out 0.05s both`,
              }}
            >
              Why Choose Our Solution?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                color: "#4c5472",
                maxWidth: "600px",
                mx: "auto",
                animation: `${fadeUp} 0.6s ease-out 0.15s both`,
              }}
            >
              Proven benefits that impact your bottom line
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 4, sm: 5, md: 6 }} sx={{ px: { xs: 0, md: 2 } }}>
            {benefitsData.map((benefit, idx) => (
              <Grid item xs={12} sm={6} md={4} key={benefit.title}>
                <Box
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    background: "#fff",
                    borderRadius: "12px",
                    boxShadow: "0 8px 24px rgba(17, 24, 39, 0.08)",
                    border: "1px solid rgba(91, 124, 250, 0.12)",
                    transition: "all 0.3s ease",
                    animation: `${slideInRight} 0.6s ease-out ${0.1 * idx}s both`,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 2,
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 16px 40px rgba(91, 124, 250, 0.12)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ fontSize: "28px" }}>{benefit.emoji}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: "20px", sm: "22px" },
                        fontWeight: 700,
                        color: "#1f2937",
                        mb: 0.5,
                      }}
                    >
                      {benefit.title}
                    </Typography>
                    <Typography sx={{ color: "#4b5563", lineHeight: 1.5, fontSize: { xs: "13px", sm: "14px" } }}>
                      {benefit.benefit}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Preparing for Success Section */}
      <Box sx={{ py: { xs: 8, sm: 10, md: 12 }, background: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, sm: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#1b1f33",
                mb: 2,
                animation: `${fadeUp} 0.6s ease-out 0.05s both`,
              }}
            >
              Preparing for your success{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                trusted source
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 3, sm: 4 }}>
            {[
              {
                icon: "🏆",
                title: "10+ Years' Experience",
                desc: "With over 10 years of experience, we deliver talent management solutions that boost workforce performance and drive growth.",
              },
              {
                icon: "🎯",
                title: "Expert Talent Strategy",
                desc: "We provide tailored talent management strategies to align with your business goals and drive success.",
              },
              {
                icon: "⭐",
                title: "Proven Success",
                desc: "Our track record of delivering impactful talent solutions speaks to our ability to enhance workforce performance.",
              },
              {
                icon: "🔄",
                title: "Continuous Support",
                desc: "We offer ongoing support to ensure seamless talent management, fostering long-term growth and retention.",
              },
            ].map((item, idx) => (
              <Grid item xs={12} sm={6} md={3} key={item.title}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: { xs: 3, sm: 3.5 },
                    minHeight: { sm: 280 },
                    background: "linear-gradient(180deg, #fff 0%, #f8faff 100%)",
                    borderRadius: "16px",
                    border: "1px solid rgba(91, 124, 250, 0.1)",
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    animation: `${scaleInCenter} 0.5s ease-out ${0.1 * idx}s both`,
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 20px 40px rgba(91, 124, 250, 0.15)",
                      "& .success-icon": {
                        transform: "scale(1.15) rotate(5deg)",
                      },
                    },
                  }}
                >
                  <Box
                    className="success-icon"
                    sx={{
                      fontSize: { xs: "48px", sm: "56px" },
                      mb: 2,
                      transition: "transform 0.4s ease",
                      animation: `${bubbleFloat} 3.5s ease-in-out infinite`,
                      animationDelay: `${0.3 * idx}s`,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: "20px", sm: "22px" },
                      fontWeight: 700,
                      color: "#1f2937",
                      mb: 1.5,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "16px", sm: "17px" },
                      color: "#4b5563",
                      lineHeight: 1.7,
                    }}
                  >
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
          py: { xs: 8, sm: 10, md: 12 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#fff",
                mb: 2,
                animation: `${fadeUp} 0.8s ease-out 0.1s both`,
              }}
            >
              Ready to Transform Your Talent Strategy?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                color: "rgba(255,255,255,0.9)",
                mb: 4,
                maxWidth: "700px",
                mx: "auto",
                lineHeight: 1.8,
                animation: `${fadeUp} 0.8s ease-out 0.2s both`,
              }}
            >
              Join hundreds of companies using our platform to build, nurture, and retain their best teams
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                component={Link}
                to="/contact"
                sx={{
                  backgroundColor: "#fff",
                  color: "#5b7cfa",
                  fontWeight: 700,
                  px: 5,
                  py: 1.5,
                  borderRadius: "12px",
                  fontSize: "16px",
                  textTransform: "none",
                  transition: "all 0.3s ease",
                  animation: `${fadeUp} 0.8s ease-out 0.3s both`,
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 24px rgba(0,0,0,0.2)",
                  },
                }}
              >
                Let's Connect
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default TalentManagement;
